import { motion } from 'framer-motion';
import { useHistory } from 'react-router';
import { css } from 'styled-components';

import { LAST_ANIMATION_DELAY } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/constants';
import { SimpleFeatureLayout } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/SimpleFeatureLayout';
import { EscriptOnboardingStepName } from 'src/app/components/Pages/EscriptOnboarding/types';
import { newOnboardingStep } from 'src/app/router/paths';

const mixBlendMultiply = css`
  mix-blend-mode: multiply;
`;

const InsuranceCardArt = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 375 283" aria-hidden className={className}>
    <circle id="ica-bg-circle" cx="188" cy="141" r="105" fill="#99BFBC" />

    <path
      id="ica-card-shadow"
      fill="#DFECEA"
      d="M277.119 76.61 85.486 87.675a9.502 9.502 0 0 0-8.938 10.033l6.572 113.829c.303 5.239 4.795 9.24 10.034 8.938l191.633-11.065c5.239-.303 9.241-4.795 8.938-10.033l-6.572-113.83a9.502 9.502 0 0 0-10.034-8.937Z"
      css={mixBlendMultiply}
    />

    <g id="ica-card" clipPath="url(#b)">
      <path
        id="ica-card-bg"
        fill="#ffffff"
        d="M280.4 72.345 88.766 83.395a9.501 9.501 0 0 0-8.94 10.032l6.564 113.829a9.502 9.502 0 0 0 10.033 8.939l191.634-11.05a9.5 9.5 0 0 0 8.939-10.032l-6.563-113.83a9.502 9.502 0 0 0-10.033-8.938Z"
      />

      <g mask="url(#c)">
        <motion.g
          animate={{
            opacity: [0, 1, 1, 1, 1, 1],
          }}
          transition={{
            ease: 'easeInOut',
            delay: LAST_ANIMATION_DELAY,
            duration: 2,
            repeatDelay: 1,
          }}
        >
          <path
            id="ica-card-text"
            fill="#C8CCD0"
            d="m198.61 186.497-94.168 5.429c-1.013.059-1.757 1.447-1.662 3.101.095 1.654.994 2.948 2.007 2.89l94.169-5.43c1.013-.059 1.757-1.447 1.662-3.101-.096-1.654-.994-2.948-2.008-2.889ZM172.347 173.988l-69.197 3.99c-.745.043-1.271 1.419-1.176 3.073.096 1.654.776 2.96 1.521 2.917l69.197-3.99c.745-.043 1.271-1.419 1.175-3.073-.095-1.654-.776-2.96-1.52-2.917Z"
          />

          <path
            id="ica-card-crest"
            fill="#74A6A2"
            fillRule="evenodd"
            d="M116.765 100.118c.075.02.145.064.285.151 3.482 2.182 7.602 3.337 11.952 3.086.869-.05 1.722-.155 2.557-.311.585-.109.878-.164 1.062-.02.184.144.201.43.234 1.002l1.207 20.942c.425 7.372-14.045 13.027-16.115 13.798l-.005.001a.89.89 0 0 1-.242.07.898.898 0 0 1-.255-.029c-2.15-.413-17.114-3.579-17.591-11.867l-1.208-20.942c-.033-.572-.05-.858.117-1.023.166-.164.463-.143 1.057-.102.847.059 1.707.065 2.576.015 4.35-.251 8.309-1.872 11.518-4.439.129-.103.194-.155.266-.183.072-.029.151-.035.308-.047a49.17 49.17 0 0 1 1.966-.114c.157-.005.236-.008.311.012Zm-1.012 6.408s6.191 3.148 11.682 2.832l.921 15.973c.173 2.994-11.078 7.651-11.078 7.651s-11.681-2.834-11.883-6.327c-.203-3.493-.921-15.973-.921-15.973 4.991-.287 11.279-4.156 11.279-4.156Z"
            clipRule="evenodd"
          />
        </motion.g>

        <path
          id="ica-card-border"
          stroke="#74A6A2"
          strokeWidth="6"
          d="m88.939 86.39 191.634-11.05a6.502 6.502 0 0 1 6.865 6.116l6.563 113.829a6.5 6.5 0 0 1-6.116 6.865L96.25 213.2a6.501 6.501 0 0 1-6.865-6.116l-6.563-113.83a6.501 6.501 0 0 1 6.117-6.865Z"
          opacity=".96"
        />
      </g>
    </g>

    <motion.g
      id="ica-checkmark-circle-group"
      animate={{ opacity: [0, 1] }}
      transition={{
        ease: 'linear',
        duration: 0.5,
        delay: LAST_ANIMATION_DELAY + 1,
      }}
    >
      <circle id="ica-checkmark-circle" cx="287" cy="78" r="27.083" fill="#008674" stroke="#fff" strokeWidth="4.167" />

      <motion.path
        id="ica-checkmark"
        stroke="#fff"
        strokeLinecap="round"
        strokeWidth="6.25"
        d="m277.625 78.781 5.707 5.469 13.043-12.5"
        animate={{
          scale: [1, 1, 1, 1.3, 0.9, 1],
          rotate: [0, 0, 0, -5, 2, 0],
        }}
        transition={{
          ease: 'linear',
          repeat: Infinity,
          repeatType: 'loop',
          delay: LAST_ANIMATION_DELAY + 2,
          duration: 1,
          repeatDelay: 4,
        }}
      />
    </motion.g>

    <defs>
      <clipPath id="b">
        <path fill="#fff" d="m79.28 83.94 211.058-12.17 7.657 132.801-211.058 12.17z" />
      </clipPath>
    </defs>
  </svg>
);

export const EscriptOnboardingInsurance = () => {
  const history = useHistory();

  return (
    <SimpleFeatureLayout
      keyName={EscriptOnboardingStepName.insurance}
      title={
        <span>
          You just pay your usual
          <br />
          co-pay
        </span>
      }
      description={`Capsule accepts all major insurance plans and will coordinate with your insurance so you don’t have to.`}
      body={{
        content: (
          <InsuranceCardArt
            css={css`
              ${({ theme }) => theme.mediaQueries.mediumDown} {
                padding-left: ${({ theme }) => theme.space.s7};
                padding-right: ${({ theme }) => theme.space.s7};
              }

              ${({ theme }) => theme.mediaQueries.medium} {
                &&& {
                  width: 70%;
                }
              }
            `}
          />
        ),
        type: 'artwork',
      }}
      cta1={{
        children: 'Continue',
        onClick: () => history.push(newOnboardingStep.toPath({ stepName: EscriptOnboardingStepName.allergies })),
      }}
    />
  );
};
